import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, CalendarRange, CheckCircle2 } from "@/components/icons";

const DAY_MS = 24 * 60 * 60 * 1000;
const fmtShort = (d) =>
  d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });

export default function FinanceWeekDetail() {
  const { weekKey } = useParams(); // YYYY-MM-DD of the Tuesday that starts the week
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/doctor/finance").then((r) => setD(r.data));
    const t = setInterval(
      () => api.get("/doctor/finance").then((r) => setD(r.data)),
      10000,
    );
    return () => clearInterval(t);
  }, []);

  const week = useMemo(() => {
    if (!d || !weekKey) return null;
    const start = new Date(weekKey + "T00:00:00");
    const end = new Date(start.getTime() + 6 * DAY_MS);
    const payout = new Date(start.getTime() + 7 * DAY_MS);
    const inRange = d.pharmacy_sales.filter((s) => {
      const dt = new Date(s.created_at);
      return dt >= start && dt < new Date(end.getTime() + DAY_MS);
    });
    const volume = inRange.reduce((a, s) => a + (s.total_amount || 0), 0);
    const earned = inRange.reduce((a, s) => a + (s.doctor_amount || 0), 0);
    const commission = inRange.reduce(
      (a, s) => a + (s.platform_commission_amount || 0),
      0,
    );
    const pending = inRange
      .filter((s) => !s.doctor_paid)
      .reduce((a, s) => a + (s.doctor_amount || 0), 0);
    return { start, end, payout, sales: inRange, volume, earned, commission, pending };
  }, [d, weekKey]);

  if (!d) return <div className="p-10 text-black">Cargando…</div>;
  if (!week || week.sales.length === 0) {
    return (
      <div className="space-y-4">
        <Link
          to="/doctor/finanzas"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-black/55 hover:text-black"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a Finanzas
        </Link>
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-10 text-center text-black/55">
          Sin ventas registradas en esta semana.
        </div>
      </div>
    );
  }

  // Aggregate by pharmacy for the summary on the right
  const byPharm = {};
  for (const s of week.sales) {
    const k = s.pharmacy_id;
    const e = (byPharm[k] ||= {
      id: k,
      name: s.pharmacy_name,
      sales: 0,
      volume: 0,
      earned: 0,
    });
    e.sales += 1;
    e.volume += s.total_amount || 0;
    e.earned += s.doctor_amount || 0;
  }
  const pharmRows = Object.values(byPharm).sort((a, b) => b.earned - a.earned);

  return (
    <div className="space-y-6" data-testid="finance-week-detail">
      <Link
        to="/doctor/finanzas"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-black/55 hover:text-black"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Finanzas
      </Link>

      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-black text-[#01BC8E] flex items-center justify-center flex-shrink-0">
            <CalendarRange className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-black/55">
              Semana
            </div>
            <h1 className="text-2xl font-extrabold text-black tracking-tight">
              {fmtShort(week.start)} → {fmtShort(week.end)}
            </h1>
            <div className="text-xs text-black/60 mt-0.5">
              Pago programado: <strong>{fmtShort(week.payout)}</strong> ·{" "}
              {week.sales.length} venta{week.sales.length === 1 ? "" : "s"} ·{" "}
              {pharmRows.length} farmacia
              {pharmRows.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Metric title="Volumen de ventas" value={`$${week.volume.toFixed(2)}`} />
        <Metric
          title="Tu parte"
          value={`$${week.earned.toFixed(2)}`}
          tone="emerald"
        />
        <Metric
          title={week.pending > 0 ? "Pendiente cobrar" : "Estado"}
          value={
            week.pending > 0 ? `$${week.pending.toFixed(2)}` : "Cobrada"
          }
          tone={week.pending > 0 ? "amber" : "emerald"}
        />
      </div>

      {/* Summary by pharmacy */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="text-lg font-extrabold text-black mb-4">
          Resumen por farmacia
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {["Farmacia", "Ventas", "Volumen", "Tu parte"].map((h) => (
                  <th
                    key={h}
                    className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pharmRows.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
                >
                  <td className="py-3 pr-3 font-bold truncate max-w-[300px]">
                    {p.name}
                  </td>
                  <td className="py-3 px-3 text-black/85">{p.sales}</td>
                  <td className="py-3 px-3 text-black/85 whitespace-nowrap">
                    ${p.volume.toFixed(2)}
                  </td>
                  <td className="py-3 pl-3 font-extrabold text-[#019A75] whitespace-nowrap">
                    ${p.earned.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail — every individual sale */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="text-lg font-extrabold text-black mb-1">
          Detalle de ventas
        </div>
        <div className="text-xs text-black/55 mb-4">
          Cada venta registrada durante esta semana.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {[
                  "Fecha",
                  "Farmacia",
                  "Venta",
                  "Comisión plataforma",
                  "Tu parte",
                  "Estado",
                ].map((h) => (
                  <th
                    key={h}
                    className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {week.sales.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
                >
                  <td className="py-3 pr-3 whitespace-nowrap text-black/85">
                    {new Date(s.created_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                    })}
                  </td>
                  <td className="py-3 px-3 truncate max-w-[260px] font-bold">
                    {s.pharmacy_name}
                  </td>
                  <td className="py-3 px-3 whitespace-nowrap">
                    ${s.total_amount.toFixed(2)}
                  </td>
                  <td className="py-3 px-3 text-black font-bold whitespace-nowrap">
                    ${s.platform_commission_amount.toFixed(2)}
                  </td>
                  <td className="py-3 px-3 text-[#019A75] font-bold whitespace-nowrap">
                    ${s.doctor_amount.toFixed(2)}
                  </td>
                  <td className="py-3 pl-3">
                    {s.doctor_paid ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-[#019A75] font-extrabold">
                        <CheckCircle2 className="w-3 h-3" /> Cobrada
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-extrabold">
                        Pendiente
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ title, value, tone }) {
  const tones = {
    amber: "text-amber-700",
    emerald: "text-[#019A75]",
  };
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold text-black/55">
        {title}
      </div>
      <div className={`text-3xl font-extrabold mt-2 ${tones[tone] || "text-black"}`}>
        {value}
      </div>
    </div>
  );
}
